import React from 'react'
import { Modal, Textarea, Button, Label, Select } from '@/shared/ui'

type Props = {
  open: boolean
  action: 'approve' | 'reject'
  saving?: boolean
  onClose: () => void
  onConfirm: (opts: {
    note: string
    visibility: 'author' | 'public'
    authorPrivateNote?: string
    thresholdPct?: number
  }) => Promise<void> | void
}

export default function SuggestionReviewModal({
  open,
  action,
  saving,
  onClose,
  onConfirm,
}: Props) {
  const [note, setNote] = React.useState('')
  const [visibility, setVisibility] =
    React.useState<'author' | 'public'>('author')
  const [authorNote, setAuthorNote] = React.useState('')
  const [threshold, setThreshold] = React.useState<number>(51)

  React.useEffect(() => {
    if (open) {
      setNote('')
      setVisibility('author')
      setAuthorNote('')
      setThreshold(51)
    }
  }, [open])

  const title =
    action === 'approve' ? 'Aprovar sugestão' : 'Negar sugestão'

  async function handleConfirm() {
    await onConfirm({
      note: note.trim(),
      visibility,
      authorPrivateNote:
        action === 'approve' ? authorNote.trim() || '' : undefined,
      thresholdPct: action === 'approve' ? threshold : undefined,
    })
  }

  return (
    <Modal open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <div className="space-y-4">
        <div className="text-lg font-semibold">{title}</div>
        <div className="text-sm text-foreground/70">
          {action === 'approve'
            ? 'Você pode registrar um motivo (opcional) sobre a aprovação desta sugestão.'
            : 'Você pode registrar um motivo (opcional) explicando por que esta sugestão foi negada.'}
        </div>

        <div className="grid gap-2">
          <Label>Motivo (opcional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Ex.: Já existe funcionalidade semelhante no módulo X; consolidaremos nessa tela."
          />
        </div>

        <div className="grid gap-2">
          <Label>Visibilidade do motivo</Label>
          <Select
            value={visibility}
            onChange={(e: any) =>
              setVisibility(
                (e.target.value || 'author') as 'author' | 'public',
              )
            }
          >
            <option value="author">Mostrar apenas para o autor</option>
            <option value="public">Mostrar para todos os usuários</option>
          </Select>
          <div className="text-xs text-foreground/60">
            Quando público, o motivo será exibido no card da sugestão para
            todos os usuários que têm acesso à listagem.
          </div>
        </div>

        {action === 'approve' && (
          <div className="grid gap-2">
            <Label>Mensagem exclusiva para o autor (opcional)</Label>
            <Textarea
              value={authorNote}
              onChange={(e) => setAuthorNote(e.target.value)}
              rows={3}
              placeholder="Ex.: Esta sugestão poderá gerar um bônus, como desconto na próxima mensalidade, quando for implementada."
            />
            <div className="text-xs text-foreground/60">
              Esta mensagem aparecerá apenas para o autor da sugestão e para o
              time master, no card de publicações em votação.
            </div>
          </div>
        )}

        {action === 'approve' && (
          <div className="grid gap-2">
            <Label>Alvo de aprovação da comunidade (%)</Label>
            <input
              type="number"
              min={1}
              max={100}
              value={threshold}
              onChange={(e) => {
                const v = Number(e.target.value)
                if (!Number.isFinite(v)) {
                  setThreshold(51)
                } else {
                  const clamped = Math.min(100, Math.max(1, Math.round(v)))
                  setThreshold(clamped)
                }
              }}
              className="h-9 w-24 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            <div className="text-xs text-foreground/60">
              Define qual porcentagem mínima de usuários elegíveis deve votar a
              favor para considerar esta sugestão aprovada pela comunidade. O
              padrão é 51%.
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            type="button"
            onClick={onClose}
            disabled={!!saving}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!!saving}>
            {saving
              ? 'Salvando...'
              : action === 'approve'
              ? 'Aprovar sugestão'
              : 'Negar sugestão'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
